import { useSelector } from 'react-redux';

export type userAccessConditionCheckType =
    | 'isTrue'
    | 'isAdmin'
    | 'isSystemAdmin'
    | 'isPowerUser'
    | 'isAdminOrPowerUser'
    | 'havePrivilege'
    | 'adminOrPrivilege'
    | 'constantExist'
    | 'constantValueEqual'
    | 'constantNotExistOrValueEqual'
    | 'atLeastOneCondition'
    | 'everyCondition';
interface userAccessCondition {
    type: userAccessConditionCheckType;
    params?: any;
}

export type userAccessConditionCheckList = {
    [key in userAccessConditionCheckType]?: any;
};

//Example to use conditions
/*
 * havePrivilege, adminOrPrivilege
 * condition: [{ type: 'havePrivilege', params: 'PRIV_PORTAL_CSS_IMAGES' }],
 *
 * isAdmin, isSystemAdmin, isPowerUser, isAdminOrPowerUser
 * condition: [{ type: 'isAdmin' }],
 *
 * constantExist
 * condition: [{ type: 'constantExist', params: 'ENABLE_METADATA_COLLECTION' }],
 *
 * constantValueEqual, constantNotExistOrValueEqual
 * condition: [{ type: 'constantValueEqual', params: {name: 'ENABLE_METADATA_COLLECTION', value: true} }],
 *
 * atLeastOneCondition
 * condition: [{ type: 'atLeastOneCondition', params: [{ type: 'isAdmin' }, { type: 'constantExist', params: 'ENABLE_METADATA_COLLECTION' }] }],
 * condition: [{
 *      type: 'atLeastOneCondition',
 *      params: [
 *          { type: 'isAdmin' },
 *          { type: 'constantExist', params: 'ENABLE_METADATA_COLLECTION' },
 *          {
 *              type: 'everyCondition',
 *              params: [
 *                  { type: 'havePrivilege', params: 'PRIV_PREFERENCES' },
 *                  { type: 'havePrivilege', params: 'PRIV_CREATE_EDIT_BURSTS' },
 *              ],
 *          },
 *      ]
 * }],
 *
 * isTrue
 * condition: [{ type: 'isTrue', params: someBooleanLogic }],
 */

export const useCheckUserAccess = () => {
    const authData = useSelector((state: any) => state.auth);
    const userInfo = authData?.userInfo;
    const userPrivileges = authData?.userInfo?.privileges;
    const globalConfigConstants = authData?.configuration?.constants;

    //Base
    const isTrue = (value: boolean) => value === true;
    const isAdmin = () => userInfo?.isAdmin;
    const isSystemAdmin = () => userInfo?.isSystemAdmin;
    const isPowerUser = () => userInfo?.isPowerUser;
    const isUserHasPrivilege = (privilegeName: string) => userPrivileges?.[privilegeName];
    const isConfigConstantExist = (constantName: string) =>
        Boolean(globalConfigConstants && globalConfigConstants.hasOwnProperty(constantName));
    const isConfigConstantEqual = (constantParams: { name: string; value: any }) =>
        isConfigConstantExist(constantParams.name) &&
        globalConfigConstants[constantParams.name] === constantParams.value;

    //Short
    const isAdminOrPowerUser = () => isAdmin() || isPowerUser();
    const isAdminOrHasPrivilege = (privilegeName: string) => isAdmin() || isUserHasPrivilege(privilegeName);
    const isConfigConstantNotExistOrValueEqual = (constantParams: { name: string; value: any }) =>
        !isConfigConstantExist(constantParams.name) || isConfigConstantEqual(constantParams);

    const conditionChecks: userAccessConditionCheckList = {
        isTrue: isTrue,
        isAdmin: isAdmin,
        isSystemAdmin: isSystemAdmin,
        isPowerUser: isPowerUser,
        isAdminOrPowerUser: isAdminOrPowerUser,
        havePrivilege: isUserHasPrivilege,
        adminOrPrivilege: isAdminOrHasPrivilege,
        constantExist: isConfigConstantExist,
        constantValueEqual: isConfigConstantEqual,
        constantNotExistOrValueEqual: isConfigConstantNotExistOrValueEqual,
    };

    //Extend check for atLeastOneCondition (for multiple child with AND condition)
    const everyCondition = (conditions: userAccessCondition[]) => {
        return conditions.every((item) => {
            return conditionChecks[item.type](item.params);
        });
    };

    const atLeastOneCondition = (conditions: userAccessCondition[]) => {
        const conditionChecksAvailable = { ...conditionChecks, everyCondition: everyCondition };
        return conditions.some((item) => {
            return conditionChecksAvailable[item.type](item.params);
        });
    };

    return { ...conditionChecks, atLeastOneCondition: atLeastOneCondition };
};
